import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { PageContainer, PageHeader } from "components/page";
import Card from "components/card";
import Button from "components/button";
import Skeleton from "components/skeleton/Skeleton";
import { ErrorState } from "components/states";
import BrandingSection from "./customize/BrandingSection";
import ButtonsSection from "./customize/ButtonsSection";
import NotificationsSection from "./customize/NotificationsSection";
import PhonePreview from "./customize/PhonePreview";
import ProfileSection from "./customize/ProfileSection";
import {
  DEFAULT_BUTTON_ORDER,
  DEFAULT_FORM,
  linkButtonId,
  profileToForm,
} from "./customize/constants";

const FORM_ID = "connect-customize-form";

const LoadingLayout = () => (
  <div className="grid grid-cols-1 gap-5 xl:grid-cols-5">
    <div className="flex flex-col gap-5 xl:col-span-3">
      <Skeleton className="h-80 w-full rounded-card" />
      <Skeleton className="h-48 w-full rounded-card" />
      <Skeleton className="h-40 w-full rounded-card" />
    </div>
    <div className="xl:col-span-2">
      <Skeleton className="h-[680px] w-full rounded-card" />
    </div>
  </div>
);

const CustomizeProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [links, setLinks] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const fetchLinks = useCallback(async () => {
    try {
      const response = await AtriumApi.get("/connect/links");
      if (response.data.success) {
        const fetchedLinks = response.data.data || [];
        setLinks(fetchedLinks);
        // Append links that are not in the saved order yet.
        setFormData((prev) => {
          const currentOrder = prev.buttonOrder || DEFAULT_BUTTON_ORDER;
          const existingIds = new Set(currentOrder);
          const newLinkIds = fetchedLinks.map(linkButtonId).filter((id) => !existingIds.has(id));
          if (newLinkIds.length === 0) return prev;
          return { ...prev, buttonOrder: [...currentOrder, ...newLinkIds] };
        });
      }
    } catch (error) {
      console.error("Error fetching links:", error);
    }
  }, []);

  const fetchSocialLinks = useCallback(async () => {
    try {
      const response = await AtriumApi.get("/connect/social-links");
      if (response.data.success) {
        setSocialLinks(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching social links:", error);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await AtriumApi.get("/connect/profile");
      if (response.data.success) {
        setFormData(profileToForm(response.data.data));
        setIsNewProfile(false);
        fetchLinks();
        fetchSocialLinks();
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No profile yet: first-run setup, prefilled from the organization.
        setIsNewProfile(true);
        const organization = error.response?.data?.organization;
        if (organization) {
          setFormData((prev) => ({
            ...prev,
            displayName: organization.name || "",
            logoUrl: organization.logoUrl || "",
          }));
        }
      } else {
        setLoadError(error);
      }
    } finally {
      setLoading(false);
    }
  }, [fetchLinks, fetchSocialLinks]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const setField = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const setButtonSetting = useCallback((buttonId, patch) => {
    setFormData((prev) => ({
      ...prev,
      buttonSettings: {
        ...prev.buttonSettings,
        [buttonId]: { ...prev.buttonSettings?.[buttonId], ...patch },
      },
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = isNewProfile
        ? await AtriumApi.post("/connect/profile", formData)
        : await AtriumApi.put("/connect/profile", formData);

      if (response.data.success) {
        track(EVENTS.LINK_HUB_CUSTOMIZED);
        notify.success(isNewProfile ? "Connect page created" : "Changes saved");
        setIsNewProfile(false);
        setTimeout(() => {
          navigate("/admin/connect");
        }, 1000);
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not save your Connect page"));
    } finally {
      setSaving(false);
    }
  };

  const ready = !loading && !loadError;

  const renderContent = () => {
    if (loading) return <LoadingLayout />;
    if (loadError) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load your Connect page settings")}
            onRetry={fetchProfile}
          />
        </Card>
      );
    }
    return (
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-5">
        <form
          id={FORM_ID}
          noValidate
          onSubmit={handleSubmit}
          className="flex flex-col gap-5 xl:col-span-3"
        >
          <ProfileSection
            displayName={formData.displayName}
            tagline={formData.tagline}
            logoUrl={formData.logoUrl}
            onFieldChange={setField}
          />
          <BrandingSection
            backgroundColor={formData.backgroundColor}
            displayNameColor={formData.displayNameColor}
            taglineColor={formData.taglineColor}
            headingFont={formData.headingFont}
            bodyFont={formData.bodyFont}
            displayName={formData.displayName}
            onFieldChange={setField}
          />
          <ButtonsSection
            buttonOrder={formData.buttonOrder}
            buttonSettings={formData.buttonSettings}
            buttonStyle={formData.buttonStyle}
            buttonColor={formData.buttonColor}
            links={links}
            onFieldChange={setField}
            onButtonSettingChange={setButtonSetting}
          />
          <NotificationsSection values={formData} onFieldChange={setField} />
        </form>

        <div className="xl:col-span-2">
          <PhonePreview formData={formData} links={links} socialLinks={socialLinks} />
        </div>
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Customize"
        description={
          isNewProfile
            ? "Create your public Connect page for visitors"
            : "Update your branding and settings"
        }
        actions={
          ready && (
            <Button type="submit" form={FORM_ID} loading={saving}>
              {saving ? "Saving…" : isNewProfile ? "Create Connect Page" : "Save Changes"}
            </Button>
          )
        }
      />
      {renderContent()}
    </PageContainer>
  );
};

export default CustomizeProfile;
